/**
 * 汇率管理中心
 * 职责: 整合所有组件，管理状态
 * 限制: <280行
 */

import React, { useState } from 'react';
import { Card, Row, Col, Spin, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { PageHeader } from '../../components/Layout/PageHeader';
import { RateCard } from './components/RateCard';
import { ChartControls } from './components/ChartControls';
import { useExchangeRateData } from './hooks/useExchangeRateData';
import { getChartOption, CURRENCY_COLORS } from './utils/chartConfig';

export const ExchangeRateDashboard: React.FC = () => {
    // 筛选状态
    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(['USD']);
    const [timeRange, setTimeRange] = useState<'week' | 'month' | 'half_year' | 'year' | 'custom'>('month');
    const [customDateRange, setCustomDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(30, 'day'),
        dayjs()
    ]);

    // 数据管理
    const {
        latestRates,
        trendData,
        loading,
        fetching,
        handleFetchRates
    } = useExchangeRateData(selectedCurrencies, timeRange, customDateRange);

    // 处理时间范围变化
    const handleTimeRangeChange = (e: any) => {
        const value = e.target.value;
        setTimeRange(value);
        if (value === 'week') setCustomDateRange([dayjs().subtract(7, 'day'), dayjs()]);
        if (value === 'month') setCustomDateRange([dayjs().subtract(30, 'day'), dayjs()]);
        if (value === 'half_year') setCustomDateRange([dayjs().subtract(180, 'day'), dayjs()]);
        if (value === 'year') setCustomDateRange([dayjs().subtract(1, 'year'), dayjs()]);
    };

    const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
        if (dates) setCustomDateRange(dates);
    };

    return (
        <div className="exchange-rate-dashboard">
            <PageHeader
                title="汇率管理中心"
                subtitle="全球汇率实时监控与趋势分析"
                actions={[{
                    key: 'fetch',
                    label: '同步最新汇率',
                    type: 'primary',
                    icon: <ReloadOutlined />,
                    onClick: handleFetchRates,
                    loading: fetching
                }]}
            />

            <div style={{ padding: '0 24px 24px 24px' }}>
                {/* 顶部关键指标卡片 */}
                <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
                    {latestRates.map(rate => (
                        <Col xs={24} sm={12} lg={6} key={rate.currency}>
                            <RateCard
                                rate={rate}
                                color={CURRENCY_COLORS[rate.currency] || '#1890ff'}
                            />
                        </Col>
                    ))}
                </Row>

                {/* 主图表区域 */}
                <Card
                    variant="borderless"
                    style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
                    styles={{ body: { padding: 24 } }}
                >
                    <ChartControls
                        selectedCurrencies={selectedCurrencies}
                        onCurrenciesChange={setSelectedCurrencies}
                        timeRange={timeRange}
                        onTimeRangeChange={handleTimeRangeChange}
                        customDateRange={customDateRange}
                        onDateRangeChange={handleDateRangeChange}
                    />

                    {/* ECharts 图表 */}
                    <Spin spinning={loading}>
                        {trendData.length > 0 ? (
                            <ReactECharts
                                option={getChartOption(trendData, selectedCurrencies)}
                                style={{ height: '450px' }}
                                notMerge={true}
                                lazyUpdate={true}
                                theme="light"
                            />
                        ) : (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="暂无趋势数据，请尝试调整日期范围或同步最新数据"
                                style={{ margin: '60px 0' }}
                            />
                        )}
                    </Spin>
                </Card>
            </div>
        </div>
    );
};

export default ExchangeRateDashboard;
